import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { ArrowLeft, LogIn, Pencil, Wifi, CircleCheck, X, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import AdminLayout from '@/layouts/AdminLayout'
import { formatDate } from '@/lib/formatters'

interface Organization {
  id: string
  name: string
  slug: string
  internal_domain: string
  status: 'active' | 'suspended'
  created_at: string
}

interface EmailConfig {
  imap_host: string
  imap_user: string
  outbound_email_provider: string
  smtp_host: string
  from_name: string
  smtp_from: string
  effective_from: string
  reply_to: string
  connection_status: string
}

interface User {
  id: string
  name: string
  email: string
  role: 'admin' | 'operator'
  status: string
  super_admin: boolean
  avatar_initials: string
  avatar_color: string
}

interface AuditLog {
  id: string
  action: string
  user_name: string | null
  created_at: string
  details: Record<string, unknown> | null
}

interface Props {
  organization: Organization
  email_config: EmailConfig
  users: User[]
  audit_logs: AuditLog[]
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-[13px] text-foreground">{value}</span>
    </div>
  )
}

export default function OrganizationsShow({ organization, email_config, users, audit_logs }: Props) {
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)

  return (
    <>
      {/* User Form Modal */}
      {userModalOpen && (
        <UserFormModal
          organizationId={organization.id}
          user={editingUser}
          onClose={() => { setUserModalOpen(false); setEditingUser(null) }}
        />
      )}

      {/* Header */}
      <header className="flex h-14 items-center gap-3 border-b px-6">
        <button
          className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent cursor-pointer"
          onClick={() => router.visit('/admin/organizations')}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="text-sm font-semibold">{organization.name}</h1>
        {organization.status === 'active' ? (
          <Badge
            variant="secondary"
            size="sm"
            className="bg-emerald-50 text-emerald-600"
          >
            Active
          </Badge>
        ) : (
          <Badge
            variant="secondary"
            size="sm"
            className="bg-red-100 text-red-800"
          >
            Suspended
          </Badge>
        )}
        <div className="flex-1" />
        <Button
          variant="default"
          size="sm"
          className="text-xs"
          onClick={() => {
            router.post(`/admin/organizations/${organization.id}/impersonate`)
          }}
        >
          <LogIn className="mr-1.5 h-3.5 w-3.5" />
          Log in as Org
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={() => router.visit(`/admin/organizations/${organization.id}/edit`)}
        >
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Edit
        </Button>
      </header>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left column */}
        <div className="flex-1 overflow-y-auto px-6 py-6 lg:px-8 lg:py-8">
          {/* Organization Details */}
          <section className="mb-8">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Organization Details
            </h2>
            <div className="divide-y rounded-lg border">
              <DetailRow label="Name" value={organization.name} />
              <DetailRow label="Slug" value={organization.slug} />
              <DetailRow label="Internal Domain" value={organization.internal_domain} />
              <DetailRow label="Created" value={formatDate(organization.created_at)} />
            </div>
          </section>

          {/* Email Configuration */}
          <section className="mb-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Email Configuration
              </h2>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-emerald-600 hover:text-emerald-700 cursor-pointer"
                onClick={() => {
                  toast.info('Connection test coming soon')
                }}
              >
                <Wifi className="mr-1.5 h-3.5 w-3.5" />
                Test Connection
              </Button>
            </div>
            <div className="divide-y rounded-lg border">
              <DetailRow label="IMAP Host" value={email_config.imap_host || '-'} />
              <DetailRow label="IMAP User" value={email_config.imap_user || '-'} />
              <DetailRow label="Delivery Provider" value={email_config.outbound_email_provider === 'postmark' ? 'Postmark' : 'Custom SMTP'} />
              <DetailRow label="SMTP Host" value={email_config.smtp_host || '-'} />
              <DetailRow label="From Name" value={email_config.from_name || '-'} />
              <DetailRow label="From Address" value={email_config.smtp_from || '-'} />
              <DetailRow label="Effective From" value={email_config.effective_from || '-'} />
              <DetailRow label="Reply-To Address" value={email_config.reply_to || '-'} />
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-muted-foreground">Status</span>
                {email_config.connection_status === 'configured' ? (
                  <Badge
                    variant="secondary"
                    size="sm"
                    className="bg-emerald-50 text-emerald-600"
                  >
                    <CircleCheck className="mr-1 h-3 w-3" />
                    Configured
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    size="sm"
                    className="bg-amber-50 text-amber-800"
                  >
                    Not Configured
                  </Badge>
                )}
              </div>
            </div>
          </section>

          {/* Audit Log */}
          {audit_logs && audit_logs.length > 0 && (
            <section>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Audit Log
              </h2>
              <div className="divide-y rounded-lg border">
                {audit_logs.map((log) => (
                  <div key={log.id} className="flex items-center gap-3 px-4 py-2.5">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-foreground">
                        <span className="font-medium">{log.user_name || 'System'}</span>
                        {' '}{log.action.replace(/_/g, ' ')}
                      </p>
                      <p className="text-[11px] text-muted-foreground">{formatDate(log.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right column — Users */}
        <div
          className="w-[420px] shrink-0 overflow-y-auto border-l px-6 py-6 bg-zinc-50"
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Users ({users.length})
            </h2>
            <Button
              variant="default"
              size="sm"
              className="text-xs"
              onClick={() => { setEditingUser(null); setUserModalOpen(true) }}
            >
              Add User
            </Button>
          </div>

          <div className="divide-y rounded-lg border bg-white">
            {users.map((user) => (
              <div key={user.id} className="flex items-center gap-3 px-4 py-3">
                {/* Avatar */}
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                  style={{ backgroundColor: user.avatar_color }}
                >
                  {user.avatar_initials}
                </div>

                {/* Name + email */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-foreground">
                    {user.name}
                    {user.super_admin && (
                      <span className="ml-1.5 text-[10px] text-amber-600 font-normal">Super Admin</span>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>

                {/* Role badge */}
                {user.role === 'admin' ? (
                  <Badge
                    variant="secondary"
                    size="sm"
                    className="shrink-0 bg-blue-50 text-[#1e3a5f]"
                  >
                    Admin
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    size="sm"
                    className="shrink-0 bg-gray-100 text-gray-600"
                  >
                    Operator
                  </Badge>
                )}

                {/* Edit button */}
                <Link
                  href={`/admin/users/${user.id}/edit`}
                  className="text-muted-foreground hover:text-foreground cursor-pointer"
                  title="Edit user"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

function UserFormModal({ organizationId, user, onClose }: { organizationId: string; user: User | null; onClose: () => void }) {
  const [name, setName] = useState(user?.name || '')
  const [email, setEmail] = useState(user?.email || '')
  const [role, setRole] = useState(user?.role || 'operator')
  const [password, setPassword] = useState('')

  const isEditing = !!user

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isEditing) {
      const payload: Record<string, string> = { name, role }
      if (password) payload.password = password
      router.patch(`/admin/organizations/${organizationId}/users/${user!.id}`, { user: payload }, {
        onSuccess: () => onClose(),
        onError: () => toast.error('Failed to update user'),
      })
    } else {
      router.post(`/admin/organizations/${organizationId}/users`, {
        user: { name, email_address: email, role, password: password || undefined },
      }, {
        onSuccess: () => onClose(),
        onError: () => toast.error('Failed to create user'),
      })
    }
  }

  function handleDeactivate() {
    if (!user) return
    if (!confirm(`Deactivate user ${user.name}?`)) return
    router.patch(`/admin/organizations/${organizationId}/users/${user.id}`, { user: { status: 'deactivated' } }, {
      onSuccess: () => onClose(),
      onError: () => toast.error('Failed to deactivate user'),
    })
  }

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={handleOverlayClick}>
      <div className="flex flex-col bg-white rounded-xl shadow-lg" style={{ width: 440 }}>
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <h2 className="text-base font-semibold text-foreground">{isEditing ? 'Edit User' : 'Add User'}</h2>
          <button onClick={onClose} className="flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted cursor-pointer">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 pb-5 space-y-4">
          <div>
            <Label htmlFor="user-name" className="text-xs">Name</Label>
            <Input id="user-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-9 text-[13px]" required />
          </div>
          {!isEditing && (
            <div>
              <Label htmlFor="user-email" className="text-xs">Email</Label>
              <Input id="user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-9 text-[13px]" required />
            </div>
          )}
          <div>
            <Label htmlFor="user-role" className="text-xs">Role</Label>
            <select
              id="user-role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'operator')}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px] shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
            >
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <Label htmlFor="user-password" className="text-xs">{isEditing ? 'New Password (leave blank to keep)' : 'Password'}</Label>
            <Input id="user-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-9 text-[13px]" {...(!isEditing ? { required: true } : {})} />
          </div>
          <div className="flex items-center justify-between pt-2">
            {isEditing ? (
              <Button type="button" variant="destructive" size="sm" onClick={handleDeactivate}>
                Deactivate
              </Button>
            ) : <div />}
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button type="submit" size="sm">{isEditing ? 'Save' : 'Create User'}</Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

OrganizationsShow.layout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
)
